<template>
  <div>
    <div class="controls controls-end d-flex flex-column">
      <div>
        <v-btn color="primary" outlined fab small :disabled="zoom >= maxZoom" @click="increaseZoom">
          <v-icon v-once>mdi-plus</v-icon>
        </v-btn>
        <v-btn class="ml-2" color="primary" outlined fab small :disabled="zoom <= minZoom" @click="decreaseZoom">
          <v-icon v-once>mdi-minus</v-icon>
        </v-btn>
      </div>
    </div>
    <div class="controls control-light controls-end d-flex flex-column ">
      <div v-if="currentDocument">
        <show-boxes-btn :key="currentDocument.id.toString()"/>
      </div>
      <show-download-doc-btn class="control-show-btn"/>
      <show-delete-doc-btn/>
    </div>
    <div v-if="showRotation" class="controls d-flex justify-start" v-bind:style="{ zoom: counterZoom }">
      <v-btn class="mr-2" color="primary" outlined fab small @click="rotate(-90)">
        <v-icon v-once>mdi-format-rotate-90</v-icon>
      </v-btn>
      <v-btn color="primary" outlined fab small @click="rotate(90)">
        <v-icon v-once style="transform: rotateY(180deg);">mdi-format-rotate-90</v-icon>
      </v-btn>
    </div>
    <ScrollingDocument
      class="pdf-document"
      v-bind:style="{ zoom }"
      v-bind="{pages, pageCount, currentPage}"
      v-slot="{page, isPageFocused, isElementFocused}"
      :enable-page-jump="true"
      @page-jump="onPageJump"
      @pages-fetch="onPagesFetch"
      @pages-reset="fitWidth"
      >
      <PDFPage
        v-bind="{scale, optimalScale, page, isPageFocused, isElementFocused, pageCount}"
        @page-rendered="onPageRendered"
        @page-errored="onPageErrored"
        @page-focus="onPageFocused"
      />
    </ScrollingDocument>
  </div>
</template>

<script>
// PDFDocument renders an entire PDF inline using
// PDF.js and <canvas>. Currently does not support,
// rendering of selected pages (but could be easily
// updated to do so).
import { mapGetters, mapActions } from "vuex"
import {PIXEL_RATIO, VIEWPORT_RATIO, PIXEL_RATIO_DEVICE} from '../utils/constants';

import ScrollingDocument from './ScrollingDocument';
import ShowBoxesBtn from './ShowBoxesBtn.vue';
import ShowDeleteDocBtn from './ShowDeleteDocBtn.vue';
import ShowDownloadDocBtn from './ShowDownloadDocBtn.vue';
import PDFPage from './PDFPage';

import { mapFields } from 'vuex-map-fields';
import { vuexPrefix } from "../store/vuex_utils";

export default {
  name: 'PDFDocument',

  data() {
    return {
      maxZoom: 3,
      minZoom: 0.1,
    }
  },

  components: {
    ScrollingDocument,
    PDFPage,
    ShowBoxesBtn,
    ShowDeleteDocBtn,
    ShowDownloadDocBtn,
  },

  props: {
    pages: {
      required: true,
    },
    pageCount: {
      type: Number,
      default: 0,
    },
    scale: {
      type: Number,
      default: 1.0,
    },
    optimalScale: {
      type: Number,
    },
    fit: {
      type: String,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    isPreviewEnabled: {
      default: false,
    },
  },

  computed: {
    defaultViewport() {
      if (!this.pages.length) return {width: 0, height:0};
      const [page] = this.pages;

      return page.getViewport(1.0);
    },

    isPortrait() {
      const {width, height} = this.defaultViewport;
      return width <= height;
    },
    showRotation() {
      return this.inRotationMode && this.rotationMode === 'all'
    },
    counterZoom() {
      return 1 / this.zoom
    },
    ...mapGetters({
      zoom: "documents/zoom",
      inRotationMode: "documents/inRotationMode",
      rotationMode: "documents/rotationMode",

      currentDocument: "documents/currentDocument",
      currentCompany: "getCurrentCompany",
      userData: 'me/userData',
      permission: 'permissions/permission',
    }),
    ...mapFields('documents',vuexPrefix('document_',['supplierdb_info', 'customerdb_info', 'kind'],'document.')),
    ...mapFields('documents', ['isBienInversionChanged', 'areIvaChecksChanged']),
  },

  methods: {
    ...mapActions({
      setZoom: "documents/setZoom",
      doRotate: 'documents/doRotate',
    }),
    rotate(deg) {
      this.doRotate({rotation: deg})
    },
    increaseZoom() {
      const zoom = Math.min(this.zoom + .1, this.maxZoom)
      this.setZoom(zoom)
    },
    decreaseZoom() {
      const zoom = Math.max(this.zoom - .1, this.minZoom)
      this.setZoom(zoom)
    },
    pageWidthScale() {
      const {defaultViewport, $el} = this;
      if (!defaultViewport.width) return 0;
      let ratio = PIXEL_RATIO;
      if (this.permission.is_readonly) {
        ratio = PIXEL_RATIO_DEVICE;
      }
      return ($el.clientWidth * ratio) * VIEWPORT_RATIO / defaultViewport.width;
    },

    pageHeightScale() {
      const {defaultViewport, $el} = this;
      if (!defaultViewport.height) return 0;
      let ratio = PIXEL_RATIO;
      if (this.permission.is_readonly) {
        ratio = PIXEL_RATIO_DEVICE;
      }
      return ($el.clientHeight * ratio) * VIEWPORT_RATIO / defaultViewport.height;
    },
    // Determine an ideal scale using viewport of document's first page, the pixel ratio from the browser
    // and a subjective scale factor based on the screen size.
    fitWidth() {
      const scale = this.pageWidthScale();
      this.updateScale(scale, {isOptimal: !this.optimalScale});
    },

    fitHeight() {
      const scale = this.isPortrait ? this.pageHeightScale() : this.pageWidthScale();
      this.updateScale(scale);
    },

    fitAuto() {
      const scale = Math.min(this.pageWidthScale(), this.pageHeightScale());
      this.updateScale(scale);
    },

    updateScale(scale, {isOptimal = false} = {}) {
      if (!scale) return;
      this.$emit('scale-change', {scale, isOptimal});
    },

    onPageJump(scrollTop) {
      this.$el.scrollTop = scrollTop; // triggers 'scroll' event
    },

    onPagesFetch(currentPage) {
      this.$parent.$emit('pages-fetch', currentPage);
    },

    onPageFocused(pageNumber) {
      this.$parent.$emit('page-focus', pageNumber);
    },

    onPageRendered(payload) {
      this.$parent.$emit('page-rendered', payload);
    },

    onPageErrored(payload) {
      this.$parent.$emit('page-errored', payload);
    },

    // closeModal(modalName) {
    //   this.$modal.hide('modal-supplier');
    // },   
  },

  watch: {
    fit(fit) {
      switch (fit) {
        case 'width':
          this.fitWidth();
          break;

        case 'auto':
          this.fitAuto();
          break;

        default:
          break;
      }
    },
    pageCount: 'fitWidth',
    isPreviewEnabled: 'fitWidth',
  },
};
</script>

<style>
.controls {
  position: absolute;
  z-index: 100;
  margin-top: 15px;
  left: 15px;
}
.controls-end {
  left: auto;
  right: 30px;
}
.controls button {
  background-color: white;
}
.pdf-document {
  position: absolute;
  overflow: auto;
  width: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: #525f69;
}

.scrolling-page {
  margin-bottom: 1em;
}

@media print {
  .pdf-document {
    position: static;
  }
}
</style>
<style scoped>
  .scrollable-card {
    /* height:600px; 
    width:1000px;  */
    height:100%; 
    width:100%;
    overflow: auto;
  }
  .primary-bckground {
    background-color: #663399;
  }
  .primary-border {
    border-color: #663399 !important;
  }
  .control-light{
    margin-top: 60px;
  }
  .control-show-btn {
    margin-bottom: 5px;
  }
</style>
